import { useMemo, useState } from 'react';
import { Box, Button, TextField, IconButton, InputAdornment, Typography, Paper } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { ProblemDetails } from '@/api/data-contracts';
import useAuth from '@fuse/core/FuseAuthProvider/useAuth';
import { useNavigate } from 'react-router';

type ValidationProblemDetails = ProblemDetails & {
	errors?: Record<string, string[]> | null;
};

type JsonProblemResponse = {
	json: () => Promise<unknown>;
};

type PasswordChangeError = {
	error?: ValidationProblemDetails | ProblemDetails | JsonProblemResponse | null;
	message?: string;
};

export default function ForceChangePasswordView() {
	const { signOut } = useAuth();
	const navigate = useNavigate();
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showCurrent, setShowCurrent] = useState(false);
	const [showNew, setShowNew] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [currentError, setCurrentError] = useState<string | null>(null);
	const [newError, setNewError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [redirecting, setRedirecting] = useState(false);

	const isLongEnough = newPassword.length >= 8;
	const hasUppercase = /[A-Z]/.test(newPassword);
	const hasLowercase = /[a-z]/.test(newPassword);
	const hasDigit = /[0-9]/.test(newPassword);
	const passwordsMatch = !!newPassword && newPassword === confirmPassword;
	const isPasswordValid = isLongEnough && hasUppercase && hasLowercase && hasDigit;

	const changePassword = useMutation({
		mutationFn: async () => {
			// Clear prior errors
			setError(null);
			setCurrentError(null);
			setNewError(null);
			setSuccess(null);

			if (!isPasswordValid) {
				setNewError('Password does not meet requirements');
				throw new Error('Password does not meet requirements');
			}

			if (!passwordsMatch) {
				setNewError('Passwords do not match');
				throw new Error('Passwords do not match');
			}

			try {
				const res = await apiClient.changeMyPassword(
					{ currentPassword, newPassword },
					{ format: 'json' } // ensure JSON parsing for ProblemDetails on error
				);
				return res.data;
			} catch (err: unknown) {
				// Normalize API error shapes (ProblemDetails or ValidationProblemDetails)
				// Case 1: HttpResponse with parsed error (from http-client with format: 'json')
				const problem = (err as PasswordChangeError | undefined)?.error ?? err;

				// Case 2: Fetch Response thrown without parsed error
				if (typeof (problem as JsonProblemResponse | undefined)?.json === 'function') {
					try {
						const body = await (problem as JsonProblemResponse).json();
						throw body; // rethrow normalized to ProblemDetails-like object
					} catch (_parseErr) {
						throw err;
					}
				}

				// At this point, problem is expected to be ProblemDetails | ValidationProblemDetails | unknown
				throw problem;
			}
		},
		onSuccess: () => {
			// Auto-clear session and redirect to sign-in so we don't get forced back here
			try {
				signOut?.();
			} catch {
				void 0;
			}
			setSuccess('Password changed successfully. Redirecting to sign-in…');
			setRedirecting(true);
			// Use replace to avoid navigating back to this page
			navigate('/sign-in', { replace: true });
		},
		onError: (err: unknown) => {
			// Attempt to extract meaningful messages and map to fields
			// ProblemDetails: { title, detail, status }
			// ValidationProblemDetails: { title, type, detail?, errors: Record<string,string[]> }
			let generalMessage: string | null = null;
			let currentFieldError: string | null = null;
			let newFieldError: string | null = null;

			const errorWithDetails = err as PasswordChangeError | undefined;
			const problem = (errorWithDetails?.error ?? err) as ValidationProblemDetails | ProblemDetails | undefined; // support HttpResponse.error or direct ProblemDetails

			// Validation errors
			const errorsDict = 'errors' in (problem ?? {}) ? (problem?.errors ?? undefined) : undefined;

			if (errorsDict && typeof errorsDict === 'object') {
				const flat = Object.values(errorsDict).flat().filter(Boolean);

				if (flat.length) {
					generalMessage = flat.join('\n');
				}
			}

			const detail = problem?.detail || problem?.title || errorWithDetails?.message;

			if (!generalMessage && typeof detail === 'string' && detail.trim().length) {
				generalMessage = detail;
			}

			// Heuristics: map common ASP.NET Identity password errors to specific fields
			const text = `${generalMessage ?? ''}`.toLowerCase();

			if (text.includes('passwordmismatch') || text.includes('incorrect password')) {
				currentFieldError = 'Current password is incorrect';
			}

			const newPwIssues: string[] = [];

			if (text.includes('passwordtooshort')) newPwIssues.push('Password is too short');

			if (text.includes('passwordrequiresnonalphanumeric')) newPwIssues.push('Include at least one symbol');

			if (text.includes('passwordrequiresdigit')) newPwIssues.push('Include at least one number');

			if (text.includes('passwordrequiresuppercase')) newPwIssues.push('Include at least one uppercase letter');

			if (text.includes('passwordrequireslowercase')) newPwIssues.push('Include at least one lowercase letter');

			if (newPwIssues.length) newFieldError = newPwIssues.join('. ');

			// Update UI state
			setCurrentError(currentFieldError);
			setNewError(newFieldError);
			setError(currentFieldError || newFieldError ? null : (generalMessage ?? 'Failed to change password'));
		}
	});

	// Disable submit conditions memoized for clarity
	const disableSubmit = useMemo(
		() =>
			redirecting ||
			!currentPassword ||
			!isPasswordValid ||
			!passwordsMatch ||
			changePassword.status === 'pending',
		[redirecting, currentPassword, isPasswordValid, passwordsMatch, changePassword.status]
	);

	return (
		<Box
			display="flex"
			justifyContent="center"
			alignItems="center"
			minHeight="100vh"
			p={2}
		>
			<Paper
				elevation={3}
				sx={{ p: 4, width: '100%', maxWidth: 420 }}
			>
				<Typography
					variant="h5"
					mb={2}
				>
					Change your password
				</Typography>
				<Typography
					color="text.secondary"
					mb={3}
				>
					You must change your password before using the app.
				</Typography>

				<TextField
					label="Current password"
					fullWidth
					type={showCurrent ? 'text' : 'password'}
					value={currentPassword}
					onChange={(e) => setCurrentPassword(e.target.value)}
					margin="normal"
					error={!!currentError}
					helperText={currentError || ' '}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									onClick={() => setShowCurrent((s) => !s)}
									edge="end"
									aria-label="toggle current password visibility"
								>
									{showCurrent ? <VisibilityOff /> : <Visibility />}
								</IconButton>
							</InputAdornment>
						)
					}}
				/>

				<TextField
					label="New password"
					fullWidth
					type={showNew ? 'text' : 'password'}
					value={newPassword}
					onChange={(e) => setNewPassword(e.target.value)}
					margin="normal"
					error={!!newError}
					helperText={newError || ' '}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									onClick={() => setShowNew((s) => !s)}
									edge="end"
									aria-label="toggle new password visibility"
								>
									{showNew ? <VisibilityOff /> : <Visibility />}
								</IconButton>
							</InputAdornment>
						)
					}}
				/>

				<Box sx={{ mt: 0.5, mb: 1 }}>
					{[
						{ label: 'At least 8 characters', met: isLongEnough },
						{ label: 'Contains an uppercase letter', met: hasUppercase },
						{ label: 'Contains a lowercase letter', met: hasLowercase },
						{ label: 'Contains a number', met: hasDigit }
					].map(({ label, met }) => (
						<Box
							key={label}
							display="flex"
							alignItems="center"
							gap={0.75}
							sx={{ py: 0.25 }}
						>
							{met ? (
								<CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
							) : (
								<RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
							)}
							<Typography
								variant="caption"
								color={met ? 'success.main' : 'text.secondary'}
							>
								{label}
							</Typography>
						</Box>
					))}
				</Box>

				<TextField
					label="Confirm new password"
					fullWidth
					type={showConfirm ? 'text' : 'password'}
					value={confirmPassword}
					onChange={(e) => setConfirmPassword(e.target.value)}
					margin="normal"
					error={!!confirmPassword && confirmPassword !== newPassword}
					helperText={confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : ' '}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									onClick={() => setShowConfirm((s) => !s)}
									edge="end"
									aria-label="toggle confirm password visibility"
								>
									{showConfirm ? <VisibilityOff /> : <Visibility />}
								</IconButton>
							</InputAdornment>
						)
					}}
				/>

				{error && (
					<Typography
						color="error"
						mt={1}
					>
						{error}
					</Typography>
				)}
				{success && (
					<Typography
						color="success.main"
						mt={1}
					>
						{success}
					</Typography>
				)}

				<Button
					variant="contained"
					color="primary"
					fullWidth
					sx={{ mt: 2 }}
					onClick={() => changePassword.mutate()}
					disabled={disableSubmit}
				>
					{changePassword.status === 'pending' ? 'Changing…' : 'Change password'}
				</Button>

				<Button
					variant="text"
					color="inherit"
					fullWidth
					sx={{ mt: 1.5 }}
					onClick={() => {
						try {
							signOut?.();
						} catch {
							void 0;
						}
						setRedirecting(true);
						navigate('/sign-in', { replace: true });
					}}
					disabled={redirecting}
				>
					Back to sign in
				</Button>
			</Paper>
		</Box>
	);
}
